import React, { useEffect, useMemo, useRef, useState } from 'react';
import Snowfall from 'react-snowfall';
import AnimatedNumber from './AnimatedNumber';
import ProgressiveFireFill from './ProgressiveFireFill';
import { getPreviewLayout, levelPalettes, progressColor, rangeProgress } from './progressivePreview/layout';
import background from '../assets/images/progressive/temple-reference-background.png';
import templeFrame from '../assets/images/progressive/temple-reference-frame.png';
import templeTitle from '../assets/images/progressive/temple-reference-title.png';
import './ProgressiveTemple.css';

const tierStyle = index => {
  const palette = levelPalettes[index] || levelPalettes[levelPalettes.length - 1];
  return { '--tier-text': palette.text, '--tier-rim': palette.rim, '--tier-surface': palette.surface,
    '--tier-light': palette.light, '--tier-metal-filter': palette.metalFilter };
};

export function ProgressiveLevelCard({ level, index, currency, onOpen, expanded = false }) {
  const progress = rangeProgress(level), color = progressColor(progress);
  const name = String(level.name || '');
  const amount = Number.parseFloat(level.winAmount) || 0;
  const Tag = expanded ? 'div' : 'button';
  return <Tag className="progressive-preview-panel" style={{ ...tierStyle(index), '--name-scale': Math.max(.35, Math.min(1, 16 / Math.max(1, name.length))) }} type={expanded ? undefined : 'button'}
    aria-label={expanded ? undefined : `Ampliar ${name}`} onClick={expanded ? undefined : onOpen}>
    <span className="panel-surface" aria-hidden="true" />
    <span className="panel-plaque-surface" aria-hidden="true" />
    <img className="panel-temple-frame" src={templeFrame} alt="" />
    <span className="panel-frame-spark panel-frame-spark-left" aria-hidden="true" />
    <span className="panel-frame-spark panel-frame-spark-right" aria-hidden="true" />
    <div className="panel-heading"><h2 className="panel-name">{name}</h2></div>
    <div className="panel-amount" aria-label={`${currency} ${amount.toFixed(2)}`}>
      <span className="panel-currency" aria-hidden="true">{currency}</span>
      <span aria-hidden="true"><AnimatedNumber value={amount} duration={900} decimals={2} /></span>
    </div>
    <div className="panel-meter">
      <div className="panel-meter-track" role="progressbar" aria-label={`Avance de ${name}`} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(progress)}>
        <span className="panel-meter-halo" aria-hidden="true" style={{ width: `${progress}%`, backgroundColor: color }} />
        <span className="panel-meter-fill" style={{ width: `${progress}%` }}>
          <ProgressiveFireFill value={progress} color={color} />
        </span>
      </div>
    </div>
  </Tag>;
}

function ProgressiveScene({ levels, currency, onOpen, covered }) {
  const host = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });
  useEffect(() => {
    const element = host.current;
    const resize = () => setSize({ width: element.clientWidth, height: element.clientHeight });
    const observer = new ResizeObserver(resize); observer.observe(element); resize();
    return () => observer.disconnect();
  }, []);
  const layout = getPreviewLayout(size.width, size.height, levels.length);
  return <div ref={host} className={`progressive-preview-scene ${covered ? 'is-covered' : ''}`} aria-hidden={covered ? true : undefined} inert={covered ? '' : undefined}>
    {size.width > 0 && levels.map((level, index) => <div className="progressive-level-2d" key={level.id} style={{
      left: `calc(50% + ${layout[index].x}px)`, top: `calc(50% - ${layout[index].y}px)`,
      width: layout[index].width, height: layout[index].height,
      '--panel-height': `${layout[index].height}px`, '--panel-width': `${layout[index].width}px`,
    }}><ProgressiveLevelCard level={level} index={index} currency={currency} onOpen={() => onOpen(level.id)} /></div>)}
  </div>;
}

// fillScreen: en la vista real el card base (con su borde dorado) llena la pantalla, sin barras negras; el preview conserva el marco 16:9.
export default function ProgressiveTemple({ levels, currency, onOpen, covered = false, fillScreen = false, title, children }) {
  const heading = (title || '').trim().toUpperCase() || 'PROGRESIVO MISTERIOSO';
  const particles = useMemo(() => {
    const image = new Image();
    image.src = '/assets/itemfall.png';
    return [image];
  }, []);
  return <section className={`progressive-preview-stage${fillScreen ? ' is-fill' : ''}`} aria-label={title ? `Progresivo ${title}` : "Progresivo misterioso"}>
    <div className="progressive-preview-artboard" style={{ backgroundImage: `url(${background})` }}>
      <Snowfall images={particles} radius={[5, 12]} snowflakeCount={window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 70} style={{ pointerEvents: 'none' }} />
      <div className="progressive-preview-heading"><img src={templeTitle} alt="" /><span>{heading}</span></div>
      <ProgressiveScene levels={levels} currency={currency} onOpen={onOpen} covered={covered} />
      {children}
    </div>
    <footer className="progressive-preview-footer">Powered by <strong>Wargos Tec</strong></footer>
  </section>;
}
